/**
 * Design-time editor for a CSV file source (`source_kind: 'file'`).
 *
 * The declaration — not the file the operator happens to upload later — is
 * what the process runs on, so this panel exists to produce it: drop a SAMPLE
 * file, the server proposes the columns and their types, and the designer
 * adjusts names, types and which are required, plus the header → column
 * mapping used on every run.
 *
 * A few sample rows are kept in the model so the Calculations and Target tabs
 * (and a plain Refresh on the Review tab) stay workable without re-uploading.
 * They are never written: executing a file process requires a real import.
 */

import { useRef, useState } from 'react'
import { FileUp, Trash2, Wand2 } from 'lucide-react'
import { inferFileColumns } from '@/api/genprocess'
import type { ColumnType, FileSource, FileSourceColumn, GenProcess } from './types'
import { applyMapping, parseCsvFile, sampleGrid } from './fileImport'
import {
  Field, SectionHeader, emptyHintStyle, hintStyle, inputStyle, selectStyle,
  tdStyle, thStyle,
} from './ui'

const COLUMN_TYPES: ColumnType[] = [
  'string', 'text', 'int', 'bigint', 'decimal', 'float',
  'boolean', 'date', 'datetime',
]

const SAMPLE_ROWS_KEPT = 20

interface Props {
  project: string
  process: GenProcess
  onChange: (patch: Partial<GenProcess>) => void
}

export default function FileSourcePanel({ project, process, onChange }: Props) {
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [lastFile, setLastFile] = useState('')
  const [preview, setPreview] = useState<Record<string, unknown>[]>([])
  const [fileHeaders, setFileHeaders] = useState<string[]>([])

  const fs: FileSource = process.file_source || { columns: [] }
  const columns = fs.columns || []
  const mapping = fs.mapping || {}
  const headers = fileHeaders.length ? fileHeaders : Object.keys(mapping)

  const patchFile = (patch: Partial<FileSource>) =>
    onChange({ file_source: { ...fs, ...patch } })

  const pickSample = async (file: File | undefined) => {
    if (!file) return
    setError('')
    setBusy(true)
    try {
      const parsed = await parseCsvFile(file)
      if (!parsed.headers.length) {
        throw new Error('That file has no header row — the first row must name the columns.')
      }
      const res = await inferFileColumns({
        project,
        headers: parsed.headers,
        rows: sampleGrid(parsed),
        // Re-sampling an already-declared source keeps the declaration and
        // only refreshes the mapping, so a designer's type edits survive.
        columns: columns.length ? columns : undefined,
      })
      const nextFs: FileSource = {
        ...fs,
        columns: res.columns,
        has_header: true,
        delimiter: fs.delimiter || ',',
        mapping: res.mapping,
      }
      const applied = applyMapping(parsed, nextFs)
      nextFs.sample_rows = applied.rows.slice(0, SAMPLE_ROWS_KEPT)
      onChange({ file_source: nextFs })
      setFileHeaders(parsed.headers)
      setPreview(applied.rows.slice(0, 8))
      setLastFile(`${parsed.filename} — ${parsed.rows.length.toLocaleString()} row(s)`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const setColumn = (i: number, patch: Partial<FileSourceColumn>) => {
    const next = columns.map((c, idx) => (idx === i ? { ...c, ...patch } : c))
    patchFile({ columns: next })
  }

  const removeColumn = (i: number) => {
    const removed = columns[i]
    const nextMapping = { ...mapping }
    for (const [h, col] of Object.entries(nextMapping)) {
      if (String(col).toLowerCase() === removed.name.toLowerCase()) delete nextMapping[h]
    }
    patchFile({ columns: columns.filter((_, idx) => idx !== i), mapping: nextMapping })
  }

  const addColumn = () => patchFile({
    columns: [...columns, { name: '', type: 'string', required: false }],
  })

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <SectionHeader>Imported columns ({columns.length})</SectionHeader>
        <div style={{ flex: 1 }} />
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.tsv,.txt,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => void pickSample(e.target.files?.[0])}
        />
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          <FileUp size={13} />{' '}
          {busy ? 'Reading…' : columns.length ? 'Re-read sample file' : 'Read a sample file'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={addColumn} disabled={busy}>
          <Wand2 size={13} /> Add column
        </button>
      </div>

      {lastFile && <div style={hintStyle}>Sampled: {lastFile}</div>}
      {error && (
        <div style={{ ...hintStyle, color: 'var(--color-danger, #d33)' }}>{error}</div>
      )}

      {!columns.length ? (
        <div style={emptyHintStyle}>
          Read a sample CSV to declare the columns this process imports. The
          sample only defines the shape — the operator picks the real file on
          the Review &amp; Execute tab each run.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Column</th>
              <th style={thStyle}>Type</th>
              <th style={{ ...thStyle, width: 80, textAlign: 'center' }}>Required</th>
              <th style={{ ...thStyle, width: 200 }}>From file column</th>
              <th style={{ ...thStyle, width: 34 }} />
            </tr>
          </thead>
          <tbody>
            {columns.map((c, i) => {
              const mappedHeader = Object.entries(mapping)
                .find(([, col]) => String(col).toLowerCase() === c.name.toLowerCase())?.[0] || ''
              return (
                <tr key={i}>
                  <td style={tdStyle}>
                    <input
                      value={c.name}
                      onChange={(e) => setColumn(i, { name: e.target.value.toLowerCase() })}
                      style={inputStyle}
                      placeholder="column name"
                    />
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={c.type}
                      onChange={(e) => setColumn(i, { type: e.target.value as ColumnType })}
                      style={selectStyle}
                    >
                      {COLUMN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!c.required}
                      onChange={(e) => setColumn(i, { required: e.target.checked })}
                    />
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={mappedHeader}
                      onChange={(e) => {
                        const next = { ...mapping }
                        for (const [h, col] of Object.entries(next)) {
                          if (String(col).toLowerCase() === c.name.toLowerCase()) delete next[h]
                        }
                        if (e.target.value) next[e.target.value.toLowerCase()] = c.name
                        patchFile({ mapping: next })
                      }}
                      style={selectStyle}
                    >
                      <option value="">match by name</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ padding: 2 }}
                      onClick={() => removeColumn(i)}
                      title="Remove this column"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div style={hintStyle}>
        Every run reads these columns from the imported file and nothing else:
        an extra column in the file is ignored, and a missing{' '}
        <strong>required</strong> column stops the run. Values are converted to
        the declared type, so a bad cell is reported with its row and column
        rather than silently coerced. Map a column to{' '}
        <em>match by name</em> to accept any header that normalises to the same
        name (<code>Employee ID</code> → <code>employee_id</code>).
      </div>

      {preview.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SectionHeader>Sample rows</SectionHeader>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{columns.map((c) => <th key={c.name} style={thStyle}>{c.name}</th>)}</tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.name} style={tdStyle}>{String(r[c.name] ?? '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Field label="Description (shown in lists; context for the AI agent)">
        <textarea
          value={process.description || ''}
          onChange={(e) => onChange({ description: e.target.value })}
          style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
        />
      </Field>
    </>
  )
}
